import { useEffect, useMemo, useState } from 'react';
import { useBreakpoint } from 'shared/hooks/use-breakpoint';

export type MobileOS = 'android' | 'ios' | null;
export type DesktopOS = 'mac' | 'windows' | 'linux' | null;
export type DeviceType = 'mobile' | 'tablet' | 'desktop';

export type UsePlatformResult = {
  // Device class
  deviceType: DeviceType;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;

  // Orientation
  isPortrait: boolean;
  isLandscape: boolean;

  // OS detection
  mobileOS: MobileOS; // android/ios on handhelds, otherwise null
  desktopOS: DesktopOS; // mac/windows/linux on desktops, otherwise null
  isAndroid: boolean;
  isIOS: boolean;
  isMac: boolean;
  isWindows: boolean;
  isLinux: boolean;
};

/**
 * usePlatform
 * Unified platform and device hook.
 * - deviceType: mobile/tablet/desktop (UA/device heuristics, not viewport width)
 * - orientation: isPortrait/isLandscape (from useBreakpoint)
 * - OS detection: android/ios (mobile), mac/windows/linux (desktop) via userAgent sniffing
 *
 * SSR-safe: returns conservative defaults when window/navigator are not available.
 */
export const usePlatform = (): UsePlatformResult => {
  const { isPortrait, isLandscape } = useBreakpoint();

  // Track width for device classification:
  // - handhelds (android/ios): use the smaller viewport side to be orientation-invariant
  // - desktops: use actual viewport width
  const [stableWidth, setStableWidth] = useState<number | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const calc = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      const useMin = mobileOS !== null;
      setStableWidth(useMin ? Math.min(w, h) : w);
    };
    calc();
    window.addEventListener('resize', calc);
    window.addEventListener('orientationchange', calc);
    return () => {
      window.removeEventListener('resize', calc);
      window.removeEventListener('orientationchange', calc);
    };
  }, []);

  // Determine deviceType strictly by width ranges
  const deviceType: DeviceType = useMemo(() => {
    const width = stableWidth;
    if (width == null) return 'desktop'; // SSR-safe default
    if (width >= 1195) return 'desktop';
    if (width >= 834) return 'tablet';
    return 'mobile';
  }, [stableWidth]);

  // OS detection (can still rely on UA; kept separate from device type)
  const { mobileOS, desktopOS } = useMemo(() => {
    const hasNavigator = typeof navigator !== 'undefined';
    const hasWindow = typeof window !== 'undefined';

    if (!hasNavigator) {
      return { mobileOS: null as MobileOS, desktopOS: null as DesktopOS };
    }

    const navAny = navigator as any;
    const ua: string = navigator.userAgent || '';
    const uaData = navAny.userAgentData || null;
    const platform: string = (uaData?.platform || navAny.platform || '') as string;

    const uaLower = ua.toLowerCase();
    const platformLower = String(platform).toLowerCase();

    const isTouchCapable = hasWindow && ('ontouchstart' in window || navAny.maxTouchPoints > 0);

    const isiPadOSMacUA = /macintosh/.test(uaLower) && isTouchCapable; // iPadOS 13+ masquerading as Mac
    const isIOSUA = /iphone|ipad|ipod/.test(uaLower) || isiPadOSMacUA;
    const isAndroidUA = /android/.test(uaLower);

    const isMacOS = /mac|macos/.test(platformLower) || /mac os x|macintosh/.test(uaLower);
    const isWindowsOS = /win/.test(platformLower) || /windows/.test(uaLower);
    const isLinuxOS = !isMacOS && !isWindowsOS && (/linux/.test(platformLower) || /linux|x11/.test(uaLower));

    const detectedMobileOS: MobileOS = isIOSUA ? 'ios' : isAndroidUA ? 'android' : null;
    const detectedDesktopOS: DesktopOS = isMacOS ? 'mac' : isWindowsOS ? 'windows' : isLinuxOS ? 'linux' : null;

    return { mobileOS: detectedMobileOS, desktopOS: detectedDesktopOS };
  }, []);

  const isAndroid = mobileOS === 'android';
  const isIOS = mobileOS === 'ios';
  const isMac = desktopOS === 'mac';
  const isWindows = desktopOS === 'windows';
  const isLinux = desktopOS === 'linux';

  return {
    deviceType,
    isMobile: deviceType === 'mobile',
    isTablet: deviceType === 'tablet',
    isDesktop: deviceType === 'desktop',

    isPortrait,
    isLandscape,

    mobileOS,
    desktopOS,
    isAndroid,
    isIOS,
    isMac,
    isWindows,
    isLinux,
  };
};

export default usePlatform;
